import { Helmet } from 'react-helmet-async';
import { Scale, Check, HelpCircle, Anchor, Bell, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function TermsOfUse() {
  return (
    <div className="pt-32 pb-24 bg-ows-white min-h-screen">
      <Helmet>
        <title>Terms of Use | Open Water Syndicate</title>
        <meta name="description" content="Terms and binding legal parameters governing memberships, vessel reservations, and maritime training operations at Open Water Syndicate." />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 text-ows-teal mb-4">
            <Scale className="h-6 w-6" />
            <span className="text-xs font-bold tracking-[0.4em] uppercase font-mono">Governing protocols // SYS-TERM-300</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-light uppercase tracking-widest text-ows-dark leading-none">
            Terms of Use
          </h1>
          <p className="mt-4 text-xs font-mono text-ows-dark/40 uppercase tracking-widest">
            Last Updated: May 25, 2026 // Active Protocol
          </p>
        </div>

        {/* Warning notification about Pre-orders */}
        <div className="mb-12 bg-ows-sand border-l-4 border-safety-orange p-8 rounded-r-[2rem] space-y-3 shadow-sm">
          <div className="flex items-center gap-2 text-safety-orange font-bold text-xs uppercase tracking-widest font-mono">
            <Bell className="h-4 w-4" /> TIMELINE PROTOCOL NOTICE (BATCH 001)
          </div>
          <p className="text-xs text-ows-dark/80 font-serif leading-relaxed">
            Please note that Open Water Syndicate does not sell or distribute physical hardware, marine boards, or vessels immediately. 
            All vessel preorders place you in an exclusive manufacturing priority pool that target delivery in 
            <strong> March 2027</strong>, subject to our system-wide Minimum Order Quantity (MOQ) criteria. Review Section 4 below 
            carefully prior to confirming checkout.
          </p>
        </div>

        {/* Outer container */}
        <div className="bg-white rounded-[3rem] border border-ows-teal/5 shadow-xl p-8 sm:p-12 space-y-12">
          {/* Section 1 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 01 // INTERACTIVE SYSTEM AGREEMENT</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">1. Acceptance of Terms</h2>
            <p className="text-sm text-ows-dark/70 font-serif leading-relaxed">
              By accessing the Open Water Syndicate Member Portal, ordering high-performance gear, booking extreme 
              expeditions, or completing electronic checkout forms, you agree to be bound legally by these Terms of Use 
              and our associated Release of Liability Waiver. If you do not agree to these protocols, you are strictly 
              forbidden from initiating any software transactions or embarking on OWS-affiliated vessels.
            </p>
          </div>

          {/* Section 2 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 02 // MEMBERSHIP SYSTEMS & PORTAL ACCESS</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">2. Membership Tiers & Credentials</h2>
            <div className="space-y-4 text-sm text-ows-dark/70 font-serif leading-relaxed">
              <p>
                OWS implements structured user categorization to manage training clearances and high-hazard launches:
              </p>
              <ul className="list-disc pl-5 space-y-2 font-sans text-xs text-ows-dark/80 leading-relaxed">
                <li>
                  <strong className="text-ows-dark">Prospect Tier:</strong> Entry-level free membership configuration. Access is limited to basic shop tools, 
                  introductory coaching schedules, and secondary logs. Upgrading to advanced profiles is governed by administrative checks.
                </li>
                <li>
                  <strong className="text-ows-dark">Syndicate Member (Ultimate):</strong> Premium, verified operational tier granting full credentials 
                  for high-altitude crossings, technical night raids, and specialized logistical coordinates.
                </li>
                <li>
                  <strong className="text-ows-dark">Credential Security:</strong> You are solely responsible for protecting your secure system-generated passkeys. 
                  Any unauthorized breach of your portal should be immediately logged through secure channels to execute key-revocation protocols.
                </li>
              </ul>
            </div>
          </div>

          {/* Section 3 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 03 // PHYSICAL VERIFICATION & MEDICAL PROTOCOLS</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">3. Maritime Safety and Operational Prerequisites</h2>
            <p className="text-sm text-ows-dark/70 font-serif leading-relaxed mb-4">
              Our waterborne activities occur under challenging environments, including commercial shipping channels and cold offshore thermals. Therefore, you agree that:
            </p>
            <ul className="list-disc pl-5 space-y-2 font-sans text-xs text-ows-dark/80 leading-relaxed">
              <li>
                <strong className="text-ows-dark">Digital Waiver Requirement:</strong> No operator is permitted to enter the water on an OWS mission or training session 
                without an actively signed, unrevoked Digital Release of Liability Waiver mapped to their profile.
              </li>
              <li>
                <strong className="text-ows-dark">Clearance Certification:</strong> To advance to higher-difficulty expeditions, operators must satisfy the requisite 
                progression checkpoints and receive public certification via the verified portal (<a href="https://cert.openwatersyndicate.com" target="_blank" rel="noopener noreferrer" className="text-ows-teal underline">cert.openwatersyndicate.com</a>).
              </li>
              <li>
                <strong className="text-ows-dark">Command Decisions:</strong> OWS guides and safety controllers hold absolute sovereign command over every launch sequence. 
                If a guide cancels or redirects an expedition due to offshore squalls, maritime wake advisories, or operator exhaustion, you will immediately comply.
              </li>
            </ul>
          </div>

          {/* Section 4 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 04 // TIMELINE PROTOCOL PRE-ORDERS</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">4. Hardware Pre-order & MOQ Logistics</h2>
            <p className="text-sm text-ows-dark/70 font-serif leading-relaxed mb-4">
              To enable customized maritime manufacturing, all physical boards (such as the premium OWS EXPEDITION CX2 and custom vessels) are governed by special fulfillment terms:
            </p>
            <div className="p-6 bg-ows-grey/60 rounded-2xl border border-ows-teal/10 space-y-3 font-sans text-xs text-ows-dark/80 leading-relaxed">
              <p>
                <strong className="text-ows-dark">A. Staged Delivery Windows:</strong> Vessel reservations place your order onto our Batch Production Schedule. 
                Our target delivery estimate is <span className="text-ows-teal font-bold">March 2027</span>.
              </p>
              <p>
                <strong className="text-ows-dark">B. Ecosystem Minimum Order Quantity (MOQ):</strong> Mass fabrication and containerized overseas marine transport require 
                a minimum ecosystem reservation total of <span className="font-bold text-ows-dark">200 boards pool-wide</span> to trigger batch dispatch. Individual orders represent 
                commitments toward securing this pool. Live progress counts are updated dynamically inside the member portal log dashboard.
              </p>
              <p>
                <strong className="text-ows-dark">C. Custom Shipping & Downtown Pickups:</strong> Checkout amounts cover hardware reservation fees only. 
                Custom regional ground shipping rates will be computed and invoiced individually when the vessels arrive at our Toronto headquarters. 
                Pickup at our <span className="font-semibold text-ows-dark">Toronto Downtown HQ</span> can be arranged via the Member Portal for a charge of $0.00 (Free local pickup).
              </p>
            </div>
          </div>

          {/* Section 5 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 05 // PROPRIETARY MARITIME DESIGNS</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">5. Intellectual Property & Hull Designs</h2>
            <p className="text-sm text-ows-dark/70 font-serif leading-relaxed">
              The dual-chamber engineering schemas, carbon-fiber composite layouts, hull stabilization specifications, and digital telemetry tools associated 
              with the OWS EXPEDITION CX2 or C2 models constitute proprietary commercial designs belonging securely to Open Water Syndicate. 
              Any unauthorized industrial reproduction, reverse-engineering attempts, or commercial duplication of training logs is legally protected by international 
              maritime design patents and trade laws.
            </p>
          </div>

          {/* Section 6 */}
          <div>
            <span className="font-mono text-[10px] text-ows-teal font-bold block mb-2">// 06 // RESPONSIBILITY TO DISCHARGE LAUNCH CLAIMS</span>
            <h2 className="text-xl font-display uppercase tracking-wider text-ows-dark mb-4">6. Indemnification</h2>
            <p className="text-sm text-ows-dark/70 font-serif leading-relaxed">
              You agree to fully defend, indemnify, and exculpate Open Water Syndicate and its directors from any external legal claims, injuries, property losses, 
              or third-party suits arising out of your unauthorized use of OWS vessels, breach of security credentials, or unsafe individual paddling decisions.
            </p>
          </div>
        </div>

        {/* Contact info block */}
        <div className="mt-12 text-center">
          <p className="text-[11px] font-mono uppercase tracking-widest text-ows-dark/40">
            Secure inquiries should be dispatched directly to: <span className="text-ows-teal font-bold underline">contact@openwatersyndicate.com</span>
          </p>
        </div>
      </div>
    </div>
  );
}
